import { useEffect, useId, useState } from 'react';
import { Button, DataTable, Icon, Panel, Tag, type Column } from '@dayon/design-system';
import { formatCount, formatPesos } from '@dayon/money';
import {
  PARKED_REASON_WORDS,
  PAYOUT_STATE_WORDS,
  type PayoutAttemptRow,
  type PayoutProviderStatus,
  type PayoutState,
  type WithdrawalPayout,
  type WithdrawalRow,
} from '@dayon/contracts';
import { api } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { PayoutActionDialog } from './PayoutActionDialog.tsx';
import {
  ACTION_WORDS,
  SECOND_PERSON_ACTIONS,
  actionsFor,
  causeOf,
  feeWords,
  handOverAnswer,
  instapayRefWords,
  payoutLabel,
  payoutTone,
  providerRefWords,
  queryFailureWords,
  stamp,
  type PayoutDriver,
  type UnparkAction,
} from './payout-state.ts';

/**
 * WHERE A RELEASED PAYOUT STANDS WITH STARPAY, in the review drawer.
 *
 * THE DATA SUPPLY:
 *   GET  /api/withdrawals/:id/payout          the row's provider fields, every hand-over, and the
 *                                             park's own sentence (`parkedDetail`, off the trail)
 *   GET  /api/payouts/provider                read only for a payout parked as NO_ORDER_FOUND, the
 *                                             one reason that offers a same-id re-send, for its flag
 * and from the funds screen, `driver`: which payout driver its own reads (the provider's status, the
 * summary) say is on, or null until one has landed — what a paid payout's InstaPay reference is said
 * to wait for depends on it.
 * THE WRITE, in PayoutActionDialog.tsx:
 *   POST /api/withdrawals/:id/payout/unpark   QUERY · CONFIRM_PAID · REFUND · RESEND_SAME_ID ·
 *                                             DISMISS_HOLD, behind funds.approve; all but the query
 *                                             carry a reason
 *
 * NOTHING IS READ FOR A ROW WITH NOTHING TO READ. A request still waiting for a
 * verdict has been sent nowhere, so the panel renders nothing for it; a rail that
 * records only has no provider record, so it says that in one sentence and asks
 * nothing. Both are decided inside the effects, not by the early returns below:
 * a return is a render, and the fetch would already have gone out.
 *
 * THE SENTENCES ARE THE SERVER'S. An action tells the `sentence` the API
 * answers with — the query's own words for whatever StarPay said — as a toast
 * when the money got where it was going, and in the error dialog when StarPay's
 * answer left the payout refused, parked or unanswered (`tellOutcome`); a
 * refusal (a query whose answer the database refuses answers 409 naming why)
 * opens the error dialog with the API's message. Nothing here composes a claim
 * about what StarPay did.
 *
 * AFTER ANY ACTION THE DRAWER READS ITS ROW AGAIN (`onChanged`), and this panel
 * its record: the row's stage, this panel and the drawer's buttons then say
 * where the payout stands now, not where it stood when the drawer opened.
 *
 * WHO MAY CLOSE IT. Confirming a payout paid, refunding it and closing its hold
 * decide what became of a release, and the admin who released it may not take
 * them (`releasedByMe`): the buttons are off for them, with the rule beside
 * them, before anybody presses.
 *
 * A FAILED READ STAYS IN THE PANEL. It is a supporting read inside a drawer the
 * operator opened for something else, and the funds screen keeps those as
 * states of the surface rather than modals over it; an ACTION that fails opens
 * the error dialog.
 *
 * "PROVED NO ORDER" IS NOT CLEARANCE TO REFUND. The stamp records that StarPay
 * confirmed it holds no order under that hand-over's number, and it can sit on an
 * attempt of a payout that is PROCESSING or PAID under a later one. It is shown
 * as a fact on the attempt; no control is offered from it.
 */

/* Every time in the drawer reads in the funds screen's own format (`stamp`, payout-state.ts). */
const when = (iso: string | null): string | null => (iso === null ? null : stamp.format(new Date(iso)));

/* The Cause row is `causeOf` in payout-state.ts, where it is tested: StarPay's acceptance
   ("success") is never shown as the cause of a payout, parked or not. */

/** The two-person rule, in the words for each action it covers. */
const SECOND_PERSON_WORDS: Partial<Record<UnparkAction, string>> = {
  CONFIRM_PAID: 'confirming it paid',
  REFUND: 'refunding it',
  DISMISS_HOLD: 'closing its hold',
};

/** "a", "a or b", "a, b or c". */
const either = (parts: readonly string[]): string =>
  parts.length <= 1 ? (parts[0] ?? '') : `${parts.slice(0, -1).join(', ')} or ${parts.at(-1)!}`;

/**
 * One row per hand-over. Four columns rather than seven: the drawer is 520px wide.
 *
 * The Answer is the latest word about the hand-over (`handOverAnswer`): a query's answer as
 * well as the send's own reply, with the path it came by and when.
 */
const attemptColumns = (state: PayoutState): Column<PayoutAttemptRow>[] => [
  {
    key: 'handover',
    header: 'Hand-over',
    render: (a) => (
      <>
        Hand-over {formatCount(a.attemptNo)}
        <div className="text-subtle tabular" style={{ fontSize: '11px', wordBreak: 'break-all' }}>
          {a.msgId}
        </div>
      </>
    ),
  },
  {
    key: 'sent',
    header: 'Sent',
    render: (a) => <span className="tabular">{stamp.format(new Date(a.sentAt))}</span>,
  },
  {
    key: 'answer',
    header: 'Answer',
    render: (a) => {
      const answer = handOverAnswer(a, state);
      return answer.source === null ? (
        <span className="text-subtle">{answer.words}</span>
      ) : (
        <>
          {answer.words}
          <div className="text-subtle tabular" style={{ fontSize: '11px' }}>
            {answer.source} · {answer.at}
          </div>
        </>
      );
    },
  },
  {
    key: 'noted',
    header: 'Noted',
    render: (a) => {
      const notes = [
        a.provedNoOrderAt === null
          ? null
          : `StarPay confirmed it holds no order under this number · ${stamp.format(new Date(a.provedNoOrderAt))}`,
        a.resentSameIdAt === null
          ? null
          : `Re-sent with the same id · ${stamp.format(new Date(a.resentSameIdAt))}`,
      ].filter((note): note is string => note !== null);
      return notes.length === 0 ? (
        <span className="text-subtle">—</span>
      ) : (
        notes.map((note) => <div key={note}>{note}</div>)
      );
    },
  },
];

/**
 * WHAT THE PANEL SAYS IS TRUE OF A PAYOUT: its state, when it was sent and paid, StarPay's reference
 * and the InstaPay reference, the fee beside the fee quoted, the cause, how many hand-overs were made,
 * when StarPay was last asked and what came of it, and every hand-over.
 *
 * Drawn from the record alone, apart from the panel's reads and actions, so that what it says can be
 * rendered and tested on its own. A row that reads "yet" makes a promise, and on a payout that has
 * ended it has to be one the platform keeps: the words of StarPay's reference, the InstaPay reference,
 * the fee and the note on asks that brought nothing usable are `providerRefWords`, `instapayRefWords`,
 * `feeWords` and `queryFailureWords` (payout-state.ts). `driver` is the payout driver the screen knows
 * is on, or null while it does not know.
 */
export function PayoutFacts({
  payout,
  driver,
}: {
  payout: WithdrawalPayout;
  driver: PayoutDriver | null;
}) {
  const cause = causeOf(payout);
  const feeDiffers =
    payout.channelFee !== null && BigInt(payout.channelFee) !== BigInt(payout.quotedFee);

  return (
    <>
      <dl className="stack" style={{ margin: 0 }}>
        <div className="def-row">
          <dt>State</dt>
          <dd>
            <Tag tone={payoutTone(payout.state)}>{PAYOUT_STATE_WORDS[payout.state]}</Tag>
          </dd>
        </div>
        <div className="def-row">
          <dt>Sent</dt>
          <dd>{when(payout.sentAt) ?? 'not yet'}</dd>
        </div>
        <div className="def-row">
          <dt>Paid</dt>
          <dd>
            {when(payout.paidAt) ??
              (payout.state === 'FAILED' || payout.state === 'REVERSED' ? 'not paid' : 'not yet')}
          </dd>
        </div>
        <div className="def-row">
          <dt>StarPay reference</dt>
          <dd className="tabular">{providerRefWords(payout)}</dd>
        </div>
        <div className="def-row">
          <dt>InstaPay reference</dt>
          <dd className="tabular">{instapayRefWords(payout, driver)}</dd>
        </div>
        <div className="def-row">
          <dt>Fee</dt>
          <dd>
            {feeWords(payout)}
            {payout.channelFeeRaw !== null ? (
              <span className="text-subtle"> · StarPay wrote {payout.channelFeeRaw}</span>
            ) : null}
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {formatPesos(BigInt(payout.quotedFee))} quoted at request
            </div>
          </dd>
        </div>
      </dl>

      {feeDiffers ? (
        <div className="notice notice-warn">
          <Icon name="warning" />
          <div>
            StarPay charged a different fee from the one quoted at request; the fee&rsquo;s unit is
            a UAT observation.
          </div>
        </div>
      ) : null}

      <dl className="stack" style={{ margin: 0 }}>
        {cause !== null ? (
          <div className="def-row">
            <dt>Cause</dt>
            <dd>{cause}</dd>
          </div>
        ) : null}
        <div className="def-row">
          <dt>Hand-overs</dt>
          <dd className="tabular">{formatCount(payout.sendAttempts)}</dd>
        </div>
        <div className="def-row">
          <dt>Last asked</dt>
          <dd>
            {when(payout.lastQueriedAt) ?? 'not asked yet'}
            {payout.queryFailures > 0 ? (
              <div className="text-subtle" style={{ fontSize: '11px' }}>
                {queryFailureWords(payout.queryFailures)}
              </div>
            ) : null}
          </dd>
        </div>
      </dl>

      {payout.attempts.length > 0 ? (
        <DataTable
          caption="Every hand-over of this payout to StarPay, with its id, when it was sent, the latest answer about it and what was noted against it"
          columns={attemptColumns(payout.state)}
          rows={payout.attempts}
          rowKey={(a) => String(a.attemptNo)}
        />
      ) : null}
    </>
  );
}

/**
 * WHAT THE PANEL DOES ONCE AN ACTION'S ROUTE HAS ANSWERED, whatever it answered. A taken action
 * closes the dialog and drops the record it changed, so its buttons cannot be pressed again while
 * the fresh one is read; a refused one leaves the dialog and the typed reason where they are. Either
 * way the panel reads its record again — a query whose answer the database refused has just parked
 * the row — and the drawer reads its row again (`onChanged`, which the drawer answers with
 * `followWithdrawal`), so the stage and the buttons beside the panel follow.
 */
export function afterPayoutAction(
  done: boolean,
  steps: { close: () => void; readRecordAgain: () => void; onChanged: () => void },
): void {
  if (done) steps.close();
  steps.readRecordAgain();
  steps.onChanged();
}

/**
 * A PARKED PAYOUT'S WAY OUT: why it waits, the actions that can settle it, and why any of them is
 * off — a role without funds.approve, the admin who released it (a second person's decision), the
 * same-id re-send's switch. Drawn from the record and the switch alone, so what is offered is what
 * the record says; nothing for a payout nobody parked.
 */
export function ParkedActions({
  payout,
  row,
  canAct,
  role,
  resendFlag,
  onAct,
}: {
  payout: WithdrawalPayout;
  row: Pick<WithdrawalRow, 'ref' | 'status'>;
  canAct: boolean;
  role: string;
  /** Whether re-sending under the same id is switched on, as the provider's status said. */
  resendFlag: 'reading' | 'on' | 'off' | 'unreadable';
  onAct: (action: UnparkAction) => void;
}) {
  const actGapId = useId();
  const resendGapId = useId();
  const twoPersonId = useId();

  const parkedReason = payout.parkedAt !== null ? payout.parkedReason : null;
  if (parkedReason === null) return null;

  /* Its reason's actions; asking and refunding for a bank return nobody has refunded; and, once the
     money is settled, asking again and closing the hold. */
  const offered = actionsFor({ parkedReason, state: payout.state }, row.status);
  const offersResend = offered.includes('RESEND_SAME_ID');
  const resendOff = resendFlag !== 'on';
  /* The actions on offer that a second person must take, when this admin released the payout. */
  const notMine = payout.releasedByMe ? offered.filter((a) => SECOND_PERSON_ACTIONS.has(a)) : [];

  return (
    <>
      <div className="notice notice-warn">
        <Icon name="warning" />
        <div>
          <strong>Waiting for an operator since {when(payout.parkedAt)}</strong> —{' '}
          {PARKED_REASON_WORDS[parkedReason]}.
          {payout.parkedDetail !== null ? (
            <div style={{ marginTop: 'var(--space-1)' }}>{payout.parkedDetail}</div>
          ) : null}
        </div>
      </div>
      <div className="row">
        {offered.map((action) => {
          const switchedOff = action === 'RESEND_SAME_ID' && resendOff;
          const secondPerson = notMine.includes(action);
          return (
            <Button
              key={action}
              small
              variant={action === 'QUERY' ? 'primary' : 'secondary'}
              disabled={!canAct || switchedOff || secondPerson}
              aria-describedby={
                !canAct ? actGapId : secondPerson ? twoPersonId : switchedOff ? resendGapId : undefined
              }
              onClick={() => onAct(action)}
            >
              {ACTION_WORDS[action].button}
            </Button>
          );
        })}
      </div>
      {!canAct ? (
        <div id={actGapId} className="text-subtle" style={{ fontSize: '11.5px' }}>
          Acting on a parked payout needs <span className="signin-perm">funds.approve</span>, which{' '}
          {role} does not hold.
        </div>
      ) : null}
      {canAct && notMine.length > 0 ? (
        <div id={twoPersonId} className="text-subtle" style={{ fontSize: '11.5px' }}>
          You released {row.ref}, so {either(notMine.map((a) => SECOND_PERSON_WORDS[a] ?? ''))} needs
          a second admin holding <span className="signin-perm">funds.approve</span>.
        </div>
      ) : null}
      {canAct && offersResend && resendOff ? (
        <div id={resendGapId} className="text-subtle" style={{ fontSize: '11.5px' }}>
          {resendFlag === 'off'
            ? 'Switched off until UAT has recorded that StarPay refuses a repeated id.'
            : resendFlag === 'unreadable'
              ? 'Whether re-sending with the same id is switched on could not be read, so it is not offered.'
              : 'Checking whether re-sending with the same id is switched on…'}
        </div>
      ) : null}
    </>
  );
}

export function PayoutPanel({
  withdrawalId,
  row,
  canAct,
  driver,
  onChanged,
}: {
  withdrawalId: string;
  row: WithdrawalRow;
  canAct: boolean;
  /** The payout driver the funds screen's reads say is on; null until one of them has landed. */
  driver: PayoutDriver | null;
  onChanged: () => void;
}) {
  const { session } = useSession();
  const role = session?.role ?? 'unknown';

  /* Released (or failed after release) and on a rail that sends: the only rows with a record. */
  const released = payoutLabel(row) !== null;
  const readable = released && row.sendable;

  /* Keyed by the withdrawal it belongs to: the drawer keeps this panel mounted from one row to the
     next, and a record must never be shown under a row it is not about. */
  const [record, setRecord] = useState<{ id: string; payout: WithdrawalPayout } | null>(null);
  const [failedFor, setFailedFor] = useState<string | null>(null);
  /** Bumped after an action, so the panel shows what the action left rather than what it found. */
  const [reads, setReads] = useState(0);

  useEffect(() => {
    if (!readable) return;
    let cancelled = false;
    api
      .get<WithdrawalPayout>(`/api/withdrawals/${encodeURIComponent(withdrawalId)}/payout`)
      .then((payout) => {
        if (cancelled) return;
        setRecord({ id: withdrawalId, payout });
        setFailedFor(null);
      })
      .catch(() => {
        if (cancelled) return;
        setRecord(null);
        setFailedFor(withdrawalId);
      });
    return () => {
      cancelled = true;
    };
  }, [withdrawalId, readable, reads]);

  const payout = record?.id === withdrawalId ? record.payout : null;
  const readFailed = failedFor === withdrawalId;
  const parkedReason = payout !== null && payout.parkedAt !== null ? payout.parkedReason : null;
  /* Its reason's actions; asking and refunding for a bank return nobody has refunded; and, once the
     money is settled, asking again and closing the hold. */
  const offered =
    payout !== null && parkedReason !== null
      ? actionsFor({ parkedReason, state: payout.state }, row.status)
      : [];
  const offersResend = offered.includes('RESEND_SAME_ID');

  /*
   * THE SAME-ID FLAG, read only where it decides something: a payout whose
   * reason offers the re-send, shown to somebody who could press it. The
   * provider read also asks StarPay for its float, so it is not made for every
   * drawer that opens. Unknown — still reading, or unreadable — keeps the
   * control off; the route refuses it while the flag is off either way.
   */
  const [resendFlag, setResendFlag] = useState<'reading' | 'on' | 'off' | 'unreadable'>('reading');
  useEffect(() => {
    if (!canAct || !offersResend) return;
    let cancelled = false;
    setResendFlag('reading');
    api
      .get<PayoutProviderStatus>('/api/payouts/provider')
      .then((status) => {
        if (!cancelled) setResendFlag(status.flags.resendSameMsgId ? 'on' : 'off');
      })
      .catch(() => {
        if (!cancelled) setResendFlag('unreadable');
      });
    return () => {
      cancelled = true;
    };
  }, [canAct, offersResend]);

  /* ── The operator's action (PayoutActionDialog.tsx) ──────────────────────── */

  const [acting, setActing] = useState<UnparkAction | null>(null);

  const settled = (done: boolean) =>
    afterPayoutAction(done, {
      close: () => {
        setActing(null);
        setRecord(null);
      },
      readRecordAgain: () => setReads((n) => n + 1),
      onChanged,
    });

  /* ── Render ─────────────────────────────────────────────────────────────── */

  if (!released) return null;

  if (!row.sendable) {
    return (
      <Panel title="Payout">
        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            Released on a rail that records only. A person sends this money; nothing was sent to a
            bank or e-wallet.
          </div>
        </div>
      </Panel>
    );
  }

  if (readFailed) {
    return (
      <Panel title="Payout">
        <div className="notice">
          <Icon name="warning-circle" />
          <div>The payout record could not be read.</div>
        </div>
      </Panel>
    );
  }

  if (payout === null) {
    return (
      <Panel title="Payout">
        <div className="skeleton" style={{ height: '120px' }}>
          &nbsp;
        </div>
      </Panel>
    );
  }

  return (
    <Panel title="Payout">
      <div className="stack">
        <PayoutFacts payout={payout} driver={driver} />

        <ParkedActions
          payout={payout}
          row={row}
          canAct={canAct}
          role={role}
          resendFlag={resendFlag}
          onAct={setActing}
        />
      </div>

      <PayoutActionDialog
        acting={acting}
        withdrawalId={withdrawalId}
        withdrawalRef={row.ref}
        onClose={() => setActing(null)}
        onSettled={settled}
      />
    </Panel>
  );
}
